import { IncomingMessage } from 'http';
import React, { createContext, useContext } from 'react';
import urlsService from '@/services/urls-service';
import { APP_ROUTES } from '@constants/constants';

export interface IFeatureToggle {
  key: string;
  value: string;
  show: boolean;
  overrides?: {
    original: string | number | '';
    source: string;
  };
}

export interface IFeatureToggles {
  [key: string]: IFeatureToggle;
}

const FeatureTogglesContext = createContext<IFeatureToggles | undefined>(undefined);

interface FeatureTogglesProviderProps {
  value: IFeatureToggles;
  children: React.ReactNode;
}

export const FeatureTogglesProvider: React.FC<FeatureTogglesProviderProps> = ({ value, children }) => (
  <FeatureTogglesContext.Provider value={value}>{children}</FeatureTogglesContext.Provider>
);

export const useFeatureToggles = (): IFeatureToggles => {
  const context = useContext(FeatureTogglesContext);
  if (!context) {
    throw new Error('useFeatureToggles must be used within a FeatureTogglesProvider');
  }
  return context;
};

export const fetchFeatureToggles = async (req: IncomingMessage | undefined): Promise<IFeatureToggles> => {
  try {
    const cookieHeader = req?.headers.cookie || '';

    const url = urlsService.getUrl('/features');
    const response = await fetch(url, {
      headers: {
        cookie: cookieHeader
      }
    });
    if (!response.ok) {
      throw new Error(`HTTP error: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching feature toggles:', error);
    throw error;
  }
};

export const useFeatureToggleValue = (key: string): IFeatureToggle | undefined => {
  const featureToggles = useFeatureToggles();
  return featureToggles[key.toLowerCase()];
};

export const isFeatureEnabled = (key: string, featureToggles: IFeatureToggles, defaultValue = false): boolean => {
  const feature = featureToggles[key.toLowerCase()];
  if (feature?.value !== undefined) {
    return feature?.value === '1';
  }
  return defaultValue;
};

export const useIsFeatureEnabled = (key: string, defaultValue = false): boolean => {
  const featureToggle = useFeatureToggleValue(key);
  if (featureToggle?.value !== undefined) {
    return featureToggle.value === '1';
  }

  return defaultValue;
};

export const useShoppingCartRoute = (): string => {
  return APP_ROUTES.SHOPPINGCART;
};

export const useCheckoutRoute = (): string => {
  return APP_ROUTES.CHECKOUT;
};
